/**
 * Markdown Preview Component with Mermaid Support
 *
 * Renders markdown content with GitHub Flavored Markdown support.
 * Supports Mermaid diagrams in ```mermaid code blocks.
 * Styled for light theme matching ebuilder.app design system.
 */

import { memo, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import mermaid from 'mermaid'

// Initialize mermaid with light theme matching ebuilder.app
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#F97316', // accent color
    primaryTextColor: '#0F172A', // foreground
    primaryBorderColor: '#E2E8F0', // border
    lineColor: '#64748B', // foreground-muted
    secondaryColor: '#F8FAFC', // background-secondary
    tertiaryColor: '#FFFFFF', // background
    background: '#FFFFFF',
    mainBkg: '#F8FAFC',
    secondBkg: '#FFFFFF',
    border1: '#E2E8F0',
    border2: '#E2E8F0',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: '13px',
  },
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
  },
  securityLevel: 'loose',
})

interface MermaidDiagramProps {
  code: string
}

/**
 * Mermaid Diagram Component
 * Renders a Mermaid diagram from code string
 */
const MermaidDiagram = memo(function MermaidDiagram({ code }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [svg, setSvg] = useState<string>('')

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code.trim()) return

      try {
        // Generate unique ID for this diagram
        const id = `mermaid-${Math.random().toString(36).substring(2, 11)}`

        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, code.trim())
        setSvg(renderedSvg)
        setError(null)
      } catch (err) {
        console.error('Mermaid render error:', err)
        setError(err instanceof Error ? err.message : 'Mermaid syntax error')
        setSvg('')
      }
    }

    renderDiagram()
  }, [code])

  if (error) {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-red-700">
          <span>⚠️</span>
          <span>Mermaid Error</span>
        </div>
        <pre className="whitespace-pre-wrap text-xs text-red-600">{error}</pre>
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-red-500">
            View source code
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-red-100 p-2 text-xs text-red-800">
            {code}
          </pre>
        </details>
      </div>
    )
  }

  if (!svg) {
    return (
      <div className="mb-4 flex items-center justify-center rounded-lg border border-border bg-background-secondary p-6">
        <span className="animate-pulse text-sm text-foreground-muted">
          Rendering diagram...
        </span>
      </div>
    )
  }

  return (
    <div className="mb-4">
      <div
        ref={containerRef}
        className="overflow-x-auto rounded-lg border border-border bg-white p-4"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  )
})

interface MarkdownPreviewProps {
  content: string
  className?: string
}

/**
 * Markdown Preview with GitHub Flavored Markdown + Mermaid
 * Supports: headings, lists, bold, italic, code, links, tables, checkboxes, mermaid diagrams
 */
export const MarkdownPreview = memo(function MarkdownPreview({
  content,
  className = '',
}: MarkdownPreviewProps) {
  if (!content.trim()) {
    return (
      <div className={`p-4 text-sm italic text-foreground-muted ${className}`}>
        No content to preview
      </div>
    )
  }

  return (
    <div className={`markdown-preview ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className="mb-4 mt-6 border-b border-border pb-2 text-xl font-semibold text-foreground first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-3 mt-5 border-b border-border/50 pb-1 text-lg font-semibold text-foreground first:mt-0">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-2 mt-4 text-base font-medium text-foreground first:mt-0">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="mb-2 mt-3 text-sm font-medium text-foreground-muted first:mt-0">
              {children}
            </h4>
          ),

          // Paragraphs
          p: ({ children }) => (
            <p className="mb-3 text-sm leading-relaxed text-foreground-muted">
              {children}
            </p>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className="mb-3 list-disc space-y-1 pl-5 text-foreground-muted">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-3 list-decimal space-y-1 pl-5 text-foreground-muted">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-sm leading-relaxed">{children}</li>
          ),

          // Inline styles
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-foreground-muted">{children}</em>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline hover:no-underline"
            >
              {children}
            </a>
          ),

          // Code - Handle mermaid blocks
          code: ({ className, children }) => {
            const isMermaid = className === 'language-mermaid'
            const isInline = !className

            // Render Mermaid diagrams
            if (isMermaid) {
              const code = String(children).replace(/\n$/, '')
              return <MermaidDiagram code={code} />
            }

            if (isInline) {
              return (
                <code className="rounded bg-background-secondary px-1.5 py-0.5 font-mono text-xs text-accent">
                  {children}
                </code>
              )
            }
            return <code className={className}>{children}</code>
          },
          pre: ({ children, node }) => {
            // Check if this pre contains a mermaid code block
            // If so, don't wrap it in pre (MermaidDiagram handles its own container)
            const codeElement = node?.children?.[0]
            const codeClassName =
              codeElement?.type === 'element'
                ? codeElement.properties?.className
                : null
            const isMermaidBlock = Array.isArray(codeClassName)
              ? codeClassName.includes('language-mermaid')
              : typeof codeClassName === 'string' &&
                codeClassName.includes('language-mermaid')

            if (
              codeElement &&
              codeElement.type === 'element' &&
              codeElement.tagName === 'code' &&
              isMermaidBlock
            ) {
              return <>{children}</>
            }

            return (
              <pre className="mb-4 overflow-x-auto rounded-lg border border-border bg-background-secondary p-3 font-mono text-xs text-foreground">
                {children}
              </pre>
            )
          },

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="mb-3 border-l-4 border-accent py-1 pl-4 italic text-foreground-muted">
              {children}
            </blockquote>
          ),

          // Horizontal rule
          hr: () => <hr className="my-6 border-border" />,

          // Tables (GFM)
          table: ({ children }) => (
            <div className="mb-4 overflow-x-auto">
              <table className="min-w-full text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-background-secondary">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-semibold text-foreground">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 text-foreground-muted">{children}</td>
          ),

          // Checkbox (GFM task lists)
          input: ({ type, checked }) => {
            if (type === 'checkbox') {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  className="mr-2"
                  style={{ accentColor: '#F97316' }}
                />
              )
            }
            return null
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
})

export default MarkdownPreview
